import React from "react";
import { HomeCinematicBackground } from "@/components/HomeCinematicBackground";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { ManifestoShowreel } from "@/components/ManifestoShowreel";
import { DirectoryChapter } from "@/components/DirectoryChapter";

/**
 * Home
 * ----
 * The entire home page is one continuous cinematic scene. A single
 * <HomeCinematicBackground /> is `position: sticky top-0 h-screen`
 * inside a tall wrapper — so as the visitor scrolls, the *same*
 * photograph (or optional video) remains fixed behind the viewport
 * across the Hero, the Manifesto, and the Directory. Each section
 * layered on top is transparent and paints only its copy + its own
 * adaptive darkening wash, so scrolling never "resets" the picture:
 * only the headline changes.
 *
 * Structure:
 *   <div.relative>                              — the tall parent
 *     <HomeCinematicBackground />               — sticky bg (100vh)
 *     <div.relative style="-mt: 100vh">         — content laid over the sticky
 *       <HeroSlideshow />       (h-screen)      — hero copy + adaptive overlay
 *       <ManifestoShowreel />   (min-h-screen)  — manifesto copy + wash
 *       <DirectoryChapter />                    — chapter grid + heavy wash + Films preview
 *     </div>
 *   </div>
 */
export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#0F0C0B]">
      <div data-testid="home-chapter" className="relative">
        <HomeCinematicBackground />

        {/* Overlay the three copy-only sections on top of the sticky background.
            The negative margin -mt-[100vh] pulls them back up over the sticky
            element so they occupy the same visual space as the photograph
            beneath. */}
        <div className="relative -mt-[100vh]">
          <HeroSlideshow />
          <ManifestoShowreel />
          <DirectoryChapter />

          {/* End-sentinel — used by <HomeCinematicBackground /> to pause the
              showreel video when the visitor scrolls into the footer. A 1 px
              tall element so it never affects layout, but IntersectionObserver
              can reliably detect when it enters/leaves the viewport. */}
          <div
            data-testid="home-chapter-end-sentinel"
            aria-hidden="true"
            className="h-px w-full"
          />
        </div>
      </div>
    </div>
  );
}
